import React, { useCallback, useEffect, useState, Fragment } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, ArrowLeft, Shield, Clock, BarChart3, CheckCircle2, Building2, DollarSign, Zap, Download, FileSpreadsheet, ArrowLeftRight, PieChart, Square, Check, X, Target, Users, Sparkles, Database, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
type SlideType = {
  id: number;
  title: string;
  subtitle?: string;
  tagline?: string;
  note: string;
  layout: string;
};
const SLIDES: SlideType[] = [{
  id: 1,
  title: 'A speed-to-quote layer for Lomas',
  subtitle: 'BlockQuote for Resorts',
  tagline: 'A standalone quoting portal that plugs cleanly into Salesforce. Cut time-to-first-quote from 24 hours to 4 — without touching your CRM or AI initiatives.',
  note: 'This deck is tailored for Vicente. Reposition BlockQuote as a quoting rail, NOT a CRM or AI-brain replacement. Lead with the 4-hour pilot KPI.',
  layout: 'title'
}, {
  id: 2,
  title: 'Your Group Business Today',
  note: 'Start with the pain. Every resort revenue leader knows these numbers — make it personal.',
  layout: 'pain'
}, {
  id: 3,
  title: 'From 24-hour quotes to 4-hour quotes',
  note: 'The transformation story. Anchor on the 4-hour target — that is the pilot KPI Vicente cares about.',
  layout: 'transformation'
}, {
  id: 4,
  title: 'Proven at Moon Palace',
  note: 'Moon Palace built this internally and proved the model. BlockQuote packages it for you — no dev team required.',
  layout: 'proof'
}, {
  id: 5,
  title: 'Three entry paths. One quoting rail.',
  note: 'Vicente was explicit: not agency-only. Show distinct lanes for Agencies, Wedding Planners, and Couples/Brides direct — all ending in the same clean flow.',
  layout: 'entry-paths'
}, {
  id: 6,
  title: 'What You Get',
  note: 'Concrete capabilities. Guardrails decide; AI assists. Every item replaces a manual process — and the Experiences Upsell is teed up as a Phase 2 option.',
  layout: 'capabilities'
}, {
  id: 7,
  title: 'Salesforce-safe by design',
  note: 'Critical Vicente slide. BlockQuote is separate from Salesforce. We sync Deal ID + status + timestamps + link. The three pilot KPIs land back in Salesforce so his team has visibility without leaving their system of record.',
  layout: 'salesforce-safe'
}, {
  id: 8,
  title: 'Your Financial Impact',
  note: 'Walk through the math with the prospect. Use their actual group revenue numbers to make it real.',
  layout: 'financials'
}, {
  id: 9,
  title: 'How We Work Together',
  note: 'The savings-share model eliminates risk. We only earn when you demonstrably save money.',
  layout: 'model'
}, {
  id: 10,
  title: 'Live in 7 weeks. Faster every month after.',
  note: 'Plain-language next steps for the Lomas team. Live in 7 weeks. After go-live, quotes get faster month over month, with the 4-hour target as the shared goal.',
  layout: 'next-steps'
}, {
  id: 11,
  title: 'How We Calculate Leakage',
  subtitle: 'Verified Savings Method',
  note: 'This appendix is designed for finance and revenue teams. Walk through the methodology to build trust and reduce objections around the savings-share model.',
  layout: 'appendix'
}];
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.08,
      duration: 0.5,
      ease: 'easeOut'
    }
  })
};
export function ResortSalesDeck() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  const slide = SLIDES[currentIndex];
  const paginate = useCallback((newDirection: number) => {
    const newIndex = currentIndex + newDirection;
    if (newIndex >= 0 && newIndex < SLIDES.length) {
      setDirection(newDirection);
      setCurrentIndex(newIndex);
    }
  }, [currentIndex]);
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') paginate(1);
      if (e.key === 'ArrowLeft') paginate(-1);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [paginate]);
  const variants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 1000 : -1000,
      opacity: 0
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1
    },
    exit: (direction: number) => ({
      zIndex: 0,
      x: direction < 0 ? 1000 : -1000,
      opacity: 0
    })
  };
  const renderSlideContent = (slide: SlideType) => {
    switch (slide.layout) {
      case 'title':
        return <div className="text-center space-y-8 flex flex-col items-center justify-center min-h-[60vh]" data-id="element-2973">
            <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            delay: 0.2
          }} className="flex items-center gap-3 justify-center" data-id="element-2974">
              <div className="w-8 h-8 bg-red-600 flex items-center justify-center" data-id="element-2975">
                <span className="font-serif text-lg font-bold text-white" data-id="element-2976">
                  B
                </span>
              </div>
              <span className="font-mono text-sm uppercase tracking-widest text-stone-400" data-id="element-2977">
                {slide.subtitle}
              </span>
            </motion.div>
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.3
          }} className="font-serif text-5xl md:text-7xl tracking-tight text-stone-50 leading-[1.1]" data-id="element-2978">
              {slide.title}
            </motion.h1>
            <motion.div initial={{
            scaleX: 0
          }} animate={{
            scaleX: 1
          }} transition={{
            delay: 0.5,
            duration: 0.8
          }} className="w-24 h-1 bg-red-600 mx-auto" data-id="element-2979" />
            <motion.p initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            delay: 0.6
          }} className="text-xl text-stone-400 max-w-2xl mx-auto" data-id="element-2980">
              {slide.tagline}
            </motion.p>
          </div>;
      case 'pain':
        return <div className="space-y-10 w-full" data-id="element-2981">
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-stone-50 border-b border-stone-800 pb-6" data-id="element-2982">
              {slide.title}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-2983">
              {[{
              icon: Clock,
              label: 'Quote Response Time',
              val: '24+',
              unit: 'hrs',
              desc: 'Today\u2019s median time-to-quote is a full day or more. Groups move on while your team is still pricing.'
            }, {
              icon: DollarSign,
              label: 'Wholesale Leakage',
              val: '15-25',
              unit: '%',
              desc: "Wholesalers capture margin on your group business. That's revenue you earned but never see."
            }, {
              icon: Shield,
              label: 'Pricing Governance',
              val: '0',
              unit: '%',
              desc: 'No audit trail. No rate floor enforcement. No visibility into what your team is quoting.'
            }].map((stat, i) => <motion.div key={i} custom={i} variants={itemVariants} initial="hidden" animate="visible" className="p-8 border border-stone-800 bg-stone-900/30 space-y-4" data-id="element-2984">
                  <stat.icon className="w-8 h-8 text-red-600" data-id="element-2985" />
                  <p className="font-mono text-xs uppercase tracking-widest text-stone-500" data-id="element-2986">
                    {stat.label}
                  </p>
                  <p className="font-mono text-5xl text-stone-50" data-id="element-2987">
                    {stat.val}
                    <span className="text-red-600 text-2xl ml-1" data-id="element-2988">
                      {stat.unit}
                    </span>
                  </p>
                  <p className="text-stone-400 leading-relaxed" data-id="element-2989">{stat.desc}</p>
                </motion.div>)}
            </div>
          </div>;
      case 'transformation':
        return <div className="space-y-10 w-full" data-id="element-2990">
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-stone-50 border-b border-stone-800 pb-6" data-id="element-2991">
              {slide.title}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 border border-stone-800" data-id="element-2992">
              <div className="border-r border-stone-800 bg-stone-900/20" data-id="element-2993">
                <div className="p-6 border-b border-stone-800" data-id="element-2994">
                  <p className="font-mono text-sm uppercase tracking-widest text-stone-500" data-id="element-2995">
                    Today
                  </p>
                </div>
                <div className="p-8 space-y-6" data-id="element-2996">
                  {['Email RFP arrives', 'Manual pricing lookup (PMS, spreadsheets)', '24+ hour response time', 'Back-and-forth negotiation', 'Group books elsewhere or via wholesaler'].map((item, i) => <motion.div key={i} custom={i} variants={itemVariants} initial="hidden" animate="visible" className="flex items-start gap-4" data-id="element-2997">
                      <span className="font-mono text-sm text-stone-600 mt-1" data-id="element-2998">
                        0{i + 1}
                      </span>
                      <p className="text-lg text-stone-400" data-id="element-2999">{item}</p>
                    </motion.div>)}
                </div>
              </div>
              <div className="bg-stone-900/60" data-id="element-3000">
                <div className="p-6 border-b border-stone-800" data-id="element-3001">
                  <p className="font-mono text-sm uppercase tracking-widest text-red-600" data-id="element-3002">
                    With BlockQuote
                  </p>
                </div>
                <div className="p-8 space-y-6" data-id="element-3003">
                  {['Agency submits request', 'AI RFP Builder structures the RFP', 'Quote options are generated & approved', 'Agency selects package & hold is placed', 'Contract & deposit tracked to Fully Booked'].map((item, i) => <motion.div key={i} custom={i + 5} variants={itemVariants} initial="hidden" animate="visible" className="flex items-start gap-4" data-id="element-3004">
                      <span className="font-mono text-sm text-red-600 mt-1" data-id="element-3005">
                        0{i + 1}
                      </span>
                      <p className="text-lg text-stone-50" data-id="element-3006">{item}</p>
                    </motion.div>)}
                </div>
                <div className="p-6 border-t border-stone-800 bg-stone-900/80" data-id="element-3007">
                  <p className="text-sm text-stone-400 italic" data-id="element-3008">
                    Target: median time-to-first-quote ≤ 4 hours. Salesforce
                    stays system of record.
                  </p>
                </div>
              </div>
            </div>
          </div>;
      case 'proof':
        return <div className="space-y-10 w-full" data-id="element-3009">
            <div data-id="element-3010">
              <p className="font-mono text-xs uppercase tracking-widest text-red-600 mb-4" data-id="element-3011">
                Proven Model
              </p>
              <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-stone-50" data-id="element-3012">
                {slide.title}
              </h2>
            </div>
            <p className="text-xl text-stone-400 max-w-3xl" data-id="element-3013">
              Moon Palace / Palace Resorts built an internal instant quoting
              portal. The results transformed their group segment economics.
              BlockQuote packages this proven playbook — no dev team required.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-4 border border-stone-800" data-id="element-3014">
              {[{
              label: 'Commission Savings',
              val: '15–25',
              unit: '%',
              desc: 'Estimated leakage shifted to direct (estimate)'
            }, {
              label: 'Estimated Annual Savings',
              val: '$6.0M',
              unit: '+',
              desc: 'Per resort group at base case adoption (estimate)'
            }, {
              label: 'Quote Speed',
              val: '<1',
              unit: 'min',
              desc: 'Down from 24+ hours'
            }, {
              label: 'Conversion',
              val: '↑',
              unit: 'Higher',
              desc: 'Speed wins group business'
            }].map((stat, i) => <motion.div key={i} custom={i} variants={itemVariants} initial="hidden" animate="visible" className={`p-8 ${i !== 3 ? 'border-b md:border-b-0 md:border-r border-stone-800' : ''}`} data-id="element-3015">
                  <p className="font-mono text-xs uppercase tracking-widest text-stone-500 mb-6" data-id="element-3016">
                    {stat.label}
                  </p>
                  <p className="font-mono text-4xl lg:text-5xl text-stone-50 mb-3" data-id="element-3017">
                    {stat.val}
                    <span className="text-red-600 text-2xl ml-1" data-id="element-3018">
                      {stat.unit}
                    </span>
                  </p>
                  <p className="text-sm text-stone-400" data-id="element-3019">{stat.desc}</p>
                </motion.div>)}
            </div>
          </div>;
      case 'entry-paths':
        return <div className="space-y-10 w-full" data-id="element-3020">
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-stone-50 border-b border-stone-800 pb-6" data-id="element-3021">
              {slide.title}
            </h2>
            <p className="text-xl text-stone-400 max-w-3xl" data-id="element-3022">
              Not agency-only. Three distinct entry paths with screens tuned to
              each user — all terminating in the same clean flow with
              policy-controlled pricing.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-3023">
              {[{
              label: 'Agency',
              title: 'Travel & wedding agencies',
              desc: 'Branded portal, account context, multi-property comparison. The fastest lane for repeat bookers.'
            }, {
              label: 'Wedding Planner',
              title: 'Independent planners',
              desc: 'Structured intake form, shareable proposal links, transparent inventory holds.'
            }, {
              label: 'Couples / Brides',
              title: 'Direct Mexican bookings',
              desc: 'Public quote form for direct guests. Instant pricing within policy. No agency intermediation required.'
            }].map((lane, i) => <motion.div key={i} custom={i} variants={itemVariants} initial="hidden" animate="visible" className="border border-stone-800 bg-stone-900/30 p-6 flex flex-col" data-id="element-3024">
                  <p className="font-mono text-xs uppercase tracking-widest text-red-600 mb-3" data-id="element-3025">
                    Lane 0{i + 1}
                  </p>
                  <h3 className="font-serif text-2xl text-stone-50 mb-2" data-id="element-3026">
                    {lane.label}
                  </h3>
                  <p className="text-sm text-stone-300 font-medium mb-3" data-id="element-3027">
                    {lane.title}
                  </p>
                  <p className="text-stone-400 text-sm leading-relaxed" data-id="element-3028">
                    {lane.desc}
                  </p>
                </motion.div>)}
            </div>

            <div className="border border-stone-800 bg-stone-950 p-6" data-id="element-3029">
              <p className="font-mono text-xs uppercase tracking-widest text-stone-500 mb-5" data-id="element-3030">
                Shared core flow
              </p>
              <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr_auto_1fr] items-center gap-4" data-id="element-3031">
                {['Request', 'Quote options', 'Hold', 'Proposal'].map((step, i) => <Fragment key={step} data-id="element-3032">
                      <div className="border border-stone-700 bg-stone-900/50 p-4 text-center" data-id="element-3033">
                        <p className="font-mono text-[10px] uppercase tracking-widest text-stone-500 mb-1" data-id="element-3034">
                          Step 0{i + 1}
                        </p>
                        <p className="font-serif text-stone-50" data-id="element-3035">{step}</p>
                      </div>
                      {i < 3 && <ArrowRight className="w-4 h-4 text-red-600 mx-auto hidden md:block" data-id="element-3036" />}
                    </Fragment>)}
              </div>
              <p className="text-xs text-stone-500 mt-5 italic" data-id="element-3037">
                Same rules, same guardrails, same audit trail — regardless of
                who walks in the door.
              </p>
            </div>
          </div>;
      case 'capabilities':
        return <div className="space-y-10 w-full" data-id="element-3038">
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-stone-50 border-b border-stone-800 pb-6" data-id="element-3039">
              {slide.title}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3040">
              {[{
              icon: Zap,
              title: 'Speed-to-Quote Portal',
              desc: 'Structured intake replaces inbound email and WhatsApp chaos. The portal is the rail — fast, consistent, governed.'
            }, {
              icon: Shield,
              title: 'Policy Engine & Rate Floors',
              desc: 'Rate floors, concession caps, blackout dates, and approval thresholds enforced on every quote — no off-policy surprises.'
            }, {
              icon: Clock,
              title: 'Smart Holds with Auto-Expiry',
              desc: 'Inventory secured automatically the moment a quote is approved. Holds release on schedule without manual chasing.'
            }, {
              icon: Building2,
              title: 'Lifecycle Nudges',
              desc: 'Automated follow-ups from quote viewed → hold expiring → deposit pending. Nothing falls through the cracks.'
            }, {
              icon: ArrowLeftRight,
              title: 'Salesforce Sync',
              desc: 'Deal ID, status, and timestamps flow back to Salesforce in near real time. Your CRM stays the system of record.'
            }, {
              icon: Sparkles,
              title: 'Experiences Upsell · Phase 2',
              desc: 'Optional layer to attach Lomas transfers and excursions to quotes — during quoting or post-hold. Won\u2019t slow the core flow.'
            }].map((item, i) => <motion.div key={i} custom={i} variants={itemVariants} initial="hidden" animate="visible" className="p-6 border border-stone-800 bg-stone-900/30 flex items-start gap-5" data-id="element-3041">
                  <item.icon className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" data-id="element-3042" />
                  <div data-id="element-3043">
                    <h3 className="text-lg font-medium text-stone-50 mb-2" data-id="element-3044">
                      {item.title}
                    </h3>
                    <p className="text-stone-400" data-id="element-3045">{item.desc}</p>
                  </div>
                </motion.div>)}
            </div>
          </div>;
      case 'salesforce-safe':
        return <div className="space-y-8 w-full" data-id="element-3046">
            <div data-id="element-3047">
              <p className="font-mono text-xs uppercase tracking-widest text-red-600 mb-4 flex items-center gap-2" data-id="element-3048">
                <Shield className="w-3.5 h-3.5 text-emerald-500" data-id="element-3049" />
                Separate · Integrated · Reversible
              </p>
              <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-stone-50" data-id="element-3050">
                {slide.title}
              </h2>
            </div>
            <p className="text-xl text-stone-400 max-w-3xl" data-id="element-3051">
              BlockQuote is not a CRM. Salesforce stays your system of record.
              We sync Deal ID, status, and timestamps — nothing else without
              your explicit toggle.
            </p>

            {/* Sync diagram */}
            <div className="border border-stone-800 bg-stone-900/30 p-6" data-id="element-3052">
              <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr_auto_1fr] items-center gap-4" data-id="element-3053">
                <div className="border border-stone-700 bg-stone-950 p-4 text-center" data-id="element-3054">
                  <Users className="w-5 h-5 mx-auto mb-2 text-stone-300" data-id="element-3055" />
                  <p className="font-mono text-[10px] uppercase tracking-widest text-stone-500 mb-1" data-id="element-3056">
                    Source
                  </p>
                  <p className="font-serif text-stone-50 text-sm" data-id="element-3057">
                    Agency / Planner / Direct
                  </p>
                </div>
                <ArrowRight className="w-5 h-5 text-red-600 hidden md:block mx-auto" data-id="element-3058" />
                <div className="border-2 border-red-600 bg-red-950/20 p-4 text-center" data-id="element-3059">
                  <Zap className="w-5 h-5 mx-auto mb-2 text-red-500" data-id="element-3060" />
                  <p className="font-mono text-[10px] uppercase tracking-widest text-red-400 mb-1" data-id="element-3061">
                    Quoting Rail
                  </p>
                  <p className="font-serif text-stone-50 text-sm" data-id="element-3062">BlockQuote</p>
                </div>
                <ArrowRight className="w-5 h-5 text-red-600 hidden md:block mx-auto" data-id="element-3063" />
                <div className="border border-stone-700 bg-stone-950 p-4 text-center" data-id="element-3064">
                  <Database className="w-5 h-5 mx-auto mb-2 text-stone-300" data-id="element-3065" />
                  <p className="font-mono text-[10px] uppercase tracking-widest text-stone-500 mb-1" data-id="element-3066">
                    System of Record
                  </p>
                  <p className="font-serif text-stone-50 text-sm" data-id="element-3067">Salesforce</p>
                </div>
              </div>
            </div>

            {/* Pilot KPIs */}
            <div data-id="element-3068">
              <p className="font-mono text-xs uppercase tracking-widest text-red-600 mb-4 flex items-center gap-2" data-id="element-3069">
                <Target className="w-3.5 h-3.5" data-id="element-3070" />
                Pilot KPI · 24h → 4h
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-3071">
                {[{
                metric: 'Median time-to-first-quote',
                detail: 'Target: ≤ 4 hours for in-policy quotes'
              }, {
                metric: 'Quote → Hold rate',
                detail: 'Conversion from first quote to held inventory'
              }, {
                metric: 'Hold → Contract rate',
                detail: 'Conversion from hold to signed contract'
              }].map((kpi, i) => <motion.div key={i} custom={i} variants={itemVariants} initial="hidden" animate="visible" className="border border-stone-800 bg-stone-900/30 p-5" data-id="element-3072">
                    <p className="font-serif text-lg text-stone-50 mb-2" data-id="element-3073">
                      {kpi.metric}
                    </p>
                    <p className="text-sm text-stone-400" data-id="element-3074">{kpi.detail}</p>
                  </motion.div>)}
              </div>
              <p className="text-xs text-stone-500 italic mt-3" data-id="element-3075">
                Every metric tied to a Deal ID and synced back to Salesforce —
                your team has visibility without leaving their system of record.
              </p>
            </div>

            {/* What we write / read */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-3076">
              <div className="border border-stone-800 bg-stone-900/30 p-5" data-id="element-3077">
                <p className="font-mono text-xs uppercase tracking-widest text-emerald-500 mb-3 flex items-center gap-2" data-id="element-3078">
                  <Check className="w-3.5 h-3.5" data-id="element-3079" /> What we write to Salesforce
                </p>
                <ul className="space-y-2 text-sm text-stone-300" data-id="element-3080">
                  {['Deal ID (immutable)', 'Status: Submitted → Quoted → Hold → Contracted → Closed Won/Lost', 'Timestamps for each transition', 'Deep link back to BlockQuote deal'].map((t, i) => <li key={i} className="flex items-start gap-2" data-id="element-3081">
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0 mt-1" data-id="element-3082" />
                      <span data-id="element-3083">{t}</span>
                    </li>)}
                </ul>
              </div>
              <div className="border border-stone-800 bg-stone-900/30 p-5" data-id="element-3084">
                <p className="font-mono text-xs uppercase tracking-widest text-stone-500 mb-3 flex items-center gap-2" data-id="element-3085">
                  <X className="w-3.5 h-3.5" data-id="element-3086" /> What we don't do
                </p>
                <ul className="space-y-2 text-sm text-stone-400" data-id="element-3087">
                  {['No CRM replacement · no pipeline ownership', 'No pricing logic inside Salesforce', 'No schema changes required day one', 'No overlap with your existing AI initiatives'].map((t, i) => <li key={i} className="flex items-start gap-2" data-id="element-3088">
                      <X className="w-3.5 h-3.5 text-stone-600 flex-shrink-0 mt-1" data-id="element-3089" />
                      <span data-id="element-3090">{t}</span>
                    </li>)}
                </ul>
              </div>
            </div>
          </div>;
      case 'financials':
        return <div className="space-y-10 w-full" data-id="element-3091">
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-stone-50 border-b border-stone-800 pb-6" data-id="element-3092">
              {slide.title}
            </h2>
            <p className="text-xl text-stone-400 max-w-3xl" data-id="element-3093">
              Where we aim to be over the first 1–2 years at a single resort
              group with ~$50M in annual group revenue. These are goals, not
              guarantees — measured against your baseline.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-3094">
              {[{
              scenario: 'Year 1 · Foundation',
              wholesale: '~15% leakage assumed',
              adoption: '~30% adoption ramp',
              savings: '$2.3M',
              detail: 'Prove the rail · land first wins'
            }, {
              scenario: 'Year 2 · Scale',
              wholesale: '~20% leakage assumed',
              adoption: '~60% adoption',
              savings: '$6.0M',
              detail: 'Scaled adoption · improvement cadence'
            }, {
              scenario: '2-Year Cumulative',
              wholesale: 'Combined goal',
              adoption: 'Year 1 + Year 2',
              savings: '$8.3M',
              detail: 'Total savings goal across the ramp'
            }].map((item, i) => <motion.div key={i} custom={i} variants={itemVariants} initial="hidden" animate="visible" className={`p-8 border ${i === 1 ? 'border-red-600 bg-red-950/20' : 'border-stone-800 bg-stone-900/30'}`} data-id="element-3095">
                  <p className={`font-mono text-xs uppercase tracking-widest mb-6 ${i === 1 ? 'text-red-600' : 'text-stone-500'}`} data-id="element-3096">
                    {item.scenario}
                  </p>
                  <p className="font-mono text-4xl text-stone-50 mb-4" data-id="element-3097">
                    {item.savings}
                  </p>
                  <p className="text-sm text-stone-500 mb-1" data-id="element-3098">
                    {item.wholesale}
                  </p>
                  <p className="text-sm text-stone-500" data-id="element-3099">{item.adoption}</p>
                  <p className="font-mono text-xs text-stone-600 mt-6" data-id="element-3100">
                    {item.detail}
                  </p>
                </motion.div>)}
            </div>
            <motion.p custom={3} variants={itemVariants} initial="hidden" animate="visible" className="font-mono text-sm text-stone-500" data-id="element-3101">
              * BQ Share = Verified savings × 5%. Numbers are 1–2 year goals,
              measured against your trailing 12-month baseline.
            </motion.p>
          </div>;
      case 'model':
        return <div className="space-y-10 w-full" data-id="element-3102">
            <div data-id="element-3103">
              <p className="font-mono text-xs uppercase tracking-widest text-red-600 mb-4" data-id="element-3104">
                Pilot commercial model · Lomas
              </p>
              <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-stone-50 border-b border-stone-800 pb-6" data-id="element-3105">
                {slide.title}
              </h2>
            </div>
            <p className="text-lg text-stone-400 max-w-3xl" data-id="element-3106">
              Terms below apply to the Lomas pilot only. No platform fees, no
              long-term commitment — we only earn when verified savings show up
              against your baseline.
            </p>
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center" data-id="element-3107">
              <div className="lg:col-span-5" data-id="element-3108">
                <motion.div initial={{
                scale: 0.9,
                opacity: 0
              }} animate={{
                scale: 1,
                opacity: 1
              }} transition={{
                duration: 0.5
              }} data-id="element-3109">
                  <p className="font-mono text-[10rem] leading-none text-stone-50 tracking-tighter" data-id="element-3110">
                    5<span className="text-red-600" data-id="element-3111">%</span>
                  </p>
                  <p className="font-mono text-lg uppercase tracking-widest text-stone-400 mt-4" data-id="element-3112">
                    of verified net savings · pilot only
                  </p>
                </motion.div>
              </div>
              <div className="lg:col-span-7 space-y-6" data-id="element-3113">
                {['Pilot deploys at no platform cost to Lomas', 'We agree a shared baseline with your finance team: trailing 12-month group room revenue and leakage %', 'We measure verified leakage reduction monthly as adoption grows', 'BlockQuote earns 5% of verified net savings during the pilot — Lomas keeps 95%'].map((step, i) => <motion.div key={i} custom={i} variants={itemVariants} initial="hidden" animate="visible" className="flex items-start gap-4 p-5 border border-stone-800 bg-stone-900/30" data-id="element-3114">
                    <span className="font-mono text-red-600 mt-0.5" data-id="element-3115">
                      0{i + 1}
                    </span>
                    <p className="text-xl text-stone-300" data-id="element-3116">{step}</p>
                  </motion.div>)}
              </div>
            </div>
          </div>;
      case 'next-steps':
        return <div className="space-y-8 w-full" data-id="element-3117">
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-stone-50 border-b border-stone-800 pb-6" data-id="element-3118">
              {slide.title}
            </h2>
            <p className="text-xl text-stone-400 max-w-3xl" data-id="element-3119">
              We get BlockQuote live in 7 weeks. After that, your team and ours
              work together each month to make quotes faster, with the 4-hour
              target as our shared goal. Success means quote times go down month
              after month.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-4 border border-stone-800" data-id="element-3120">
              {[{
              phase: 'Weeks 1–2',
              title: 'Foundation',
              desc: 'NDA · pilot owners assigned · shared drive · stakeholder kickoff · baseline agreed'
            }, {
              phase: 'Weeks 3–4',
              title: 'Configuration',
              desc: 'Rate sheets · blackouts · concession matrix · approval thresholds · Salesforce sync map'
            }, {
              phase: 'Weeks 5–6',
              title: 'Shadow Test',
              desc: 'Real requests run through BlockQuote · outputs compared to manual · tuning pass'
            }, {
              phase: 'Week 7',
              title: 'Go Live',
              desc: 'Agencies, planners, and direct lane onboarded · monthly KPI cadence · first improvement target set'
            }].map((step, i) => <motion.div key={i} custom={i} variants={itemVariants} initial="hidden" animate="visible" className={`p-7 ${i !== 3 ? 'border-b md:border-b-0 md:border-r border-stone-800' : ''} ${i === 3 ? 'bg-red-950/10' : ''}`} data-id="element-3121">
                  <p className={`font-mono text-xs uppercase tracking-widest mb-2 ${i === 3 ? 'text-red-600' : 'text-stone-500'}`} data-id="element-3122">
                    {step.phase}
                  </p>
                  <h3 className="font-serif text-2xl text-stone-50 mb-3" data-id="element-3123">
                    {step.title}
                  </h3>
                  <p className="text-stone-400 leading-relaxed text-sm" data-id="element-3124">
                    {step.desc}
                  </p>
                </motion.div>)}
            </div>

            <div data-id="element-3125">
              <p className="font-mono text-xs uppercase tracking-widest text-red-600 mb-4" data-id="element-3126">
                For next Thursday · Three deliverables
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-3127">
                {[{
                num: 'A',
                title: 'Updated walkthrough + screens',
                desc: 'The three entry-path journeys (Agency / Planner / Couples-Direct) wired end-to-end.'
              }, {
                num: 'B',
                title: 'Salesforce integration map',
                desc: 'Exactly what we write, what we read, and how Deal IDs flow back to your CRM.'
              }, {
                num: 'C',
                title: 'Pilot plan · 4-hour target',
                desc: 'Week-by-week with owners, the three KPIs, and the success criterion.'
              }].map((d, i) => <motion.div key={i} custom={i + 4} variants={itemVariants} initial="hidden" animate="visible" className="border border-stone-800 bg-stone-900/30 p-5" data-id="element-3128">
                    <p className="font-mono text-red-600 text-sm mb-2" data-id="element-3129">
                      {d.num}
                    </p>
                    <p className="font-serif text-lg text-stone-50 mb-2" data-id="element-3130">
                      {d.title}
                    </p>
                    <p className="text-sm text-stone-400 leading-relaxed" data-id="element-3131">
                      {d.desc}
                    </p>
                  </motion.div>)}
              </div>
            </div>

            <motion.div custom={8} variants={itemVariants} initial="hidden" animate="visible" className="p-6 border border-red-900/30 bg-red-950/10 flex items-center gap-4" data-id="element-3132">
              <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0" data-id="element-3133" />
              <div data-id="element-3134">
                <p className="font-serif text-xl text-stone-50" data-id="element-3135">
                  Start at Destination Royale. Get faster every month until we
                  hit 4-hour quoting together.
                </p>
                <p className="text-stone-400 text-sm mt-1" data-id="element-3136">
                  Salesforce stays system of record throughout. Your CRM and AI
                  initiatives are unaffected.
                </p>
              </div>
            </motion.div>
          </div>;
      case 'appendix':
        return <div className="space-y-10 w-full" data-id="element-3137">
            {/* Header */}
            <div data-id="element-3138">
              <p className="font-mono text-xs uppercase tracking-widest text-red-600 mb-4" data-id="element-3139">
                Appendix
              </p>
              <h2 className="font-serif text-4xl md:text-5xl tracking-tight text-stone-50" data-id="element-3140">
                {slide.title}
              </h2>
            </div>

            {/* Intro */}
            <p className="text-lg text-stone-400 max-w-3xl leading-relaxed" data-id="element-3141">
              Leakage is the hidden cost of third-party distribution in group
              travel — margin captured by wholesalers, bedbanks, and tour
              operators that belongs on your bottom line. BlockQuote.ai only
              charges on savings that are measured and verified against an
              agreed baseline with your finance team.
            </p>

            {/* Formula Callout */}
            <div className="border border-red-600/30 bg-red-950/10 p-6" data-id="element-3142">
              <p className="font-mono text-xs uppercase tracking-widest text-red-600 mb-4" data-id="element-3143">
                Core Formulas
              </p>
              <div className="space-y-3 font-mono text-lg" data-id="element-3144">
                <p className="text-stone-50" data-id="element-3145">
                  <span className="text-stone-500 text-sm mr-3" data-id="element-3146">1</span>
                  Leakage $ = <span className="text-red-500" data-id="element-3147">GRR</span> ×{' '}
                  <span className="text-red-500" data-id="element-3148">Wholesale Leakage %</span>
                </p>
                <p className="text-stone-50" data-id="element-3149">
                  <span className="text-stone-500 text-sm mr-3" data-id="element-3150">2</span>
                  Verified Savings $ = Leakage $ ×{' '}
                  <span className="text-red-500" data-id="element-3151">Adoption %</span>
                </p>
                <p className="text-stone-50" data-id="element-3152">
                  <span className="text-stone-500 text-sm mr-3" data-id="element-3153">3</span>
                  BQ Share $ = Verified Savings $ ×{' '}
                  <span className="text-red-500" data-id="element-3154">5%</span>
                </p>
              </div>
            </div>

            {/* Definitions */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-3155">
              {[{
              term: 'GRR',
              def: 'Group Room Revenue — annual room revenue from group bookings (weddings, incentives, retreats, celebrations).'
            }, {
              term: 'Wholesale Leakage %',
              def: 'Effective % of GRR lost to wholesalers/bedbanks via margin, net-rate discounts, overrides, or rebates.'
            }, {
              term: 'Adoption %',
              def: '% of GRR routed through BlockQuote.ai (quotes → holds → contracts → deposits).'
            }, {
              term: 'BQ Share',
              def: 'Default 5% of Verified Savings. Measured monthly/quarterly against agreed baseline.'
            }].map((item, i) => <motion.div key={i} custom={i} variants={itemVariants} initial="hidden" animate="visible" className="p-4 border border-stone-800 bg-stone-900/30" data-id="element-3156">
                  <p className="font-mono text-xs text-red-600 uppercase tracking-widest mb-2" data-id="element-3157">
                    {item.term}
                  </p>
                  <p className="text-sm text-stone-400 leading-relaxed" data-id="element-3158">
                    {item.def}
                  </p>
                </motion.div>)}
            </div>

            {/* 3 Method Cards */}
            <div data-id="element-3159">
              <p className="font-mono text-xs uppercase tracking-widest text-stone-500 mb-6" data-id="element-3160">
                Calculation Methods
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-3161">
                {/* Method A */}
                <motion.div custom={0} variants={itemVariants} initial="hidden" animate="visible" className="border border-stone-800 bg-stone-900/30 flex flex-col" data-id="element-3162">
                  <div className="p-5 border-b border-stone-800 flex items-center gap-3" data-id="element-3163">
                    <FileSpreadsheet className="w-5 h-5 text-red-600 flex-shrink-0" data-id="element-3164" />
                    <div data-id="element-3165">
                      <p className="font-mono text-xs text-red-600 uppercase tracking-widest" data-id="element-3166">
                        Method A
                      </p>
                      <p className="text-stone-50 font-medium text-sm mt-0.5" data-id="element-3167">
                        Finance Line Items
                      </p>
                    </div>
                    <span className="ml-auto font-mono text-[10px] text-emerald-500 border border-emerald-500/30 px-1.5 py-0.5 uppercase tracking-widest" data-id="element-3168">
                      Most Accurate
                    </span>
                  </div>
                  <div className="p-5 space-y-3 flex-1" data-id="element-3169">
                    <p className="text-sm text-stone-400" data-id="element-3170">
                      Leakage % = (Total third-party costs on group bookings) ÷
                      (Total GRR)
                    </p>
                    <p className="text-sm text-stone-400" data-id="element-3171">
                      Third-party costs include: wholesaler/bedbank margin,
                      commissions, overrides, rebates, net-rate discounts tied
                      to group bookings
                    </p>
                    <p className="text-sm text-stone-400" data-id="element-3172">
                      Use trailing 12 months as the baseline
                    </p>
                  </div>
                  <div className="p-4 border-t border-stone-800 bg-stone-900/50" data-id="element-3173">
                    <p className="font-mono text-xs text-stone-500" data-id="element-3174">
                      <span className="text-stone-300" data-id="element-3175">Example:</span> GRR $50M,
                      third-party costs $4M → Leakage = 8%
                    </p>
                  </div>
                </motion.div>

                {/* Method B */}
                <motion.div custom={1} variants={itemVariants} initial="hidden" animate="visible" className="border border-stone-800 bg-stone-900/30 flex flex-col" data-id="element-3176">
                  <div className="p-5 border-b border-stone-800 flex items-center gap-3" data-id="element-3177">
                    <ArrowLeftRight className="w-5 h-5 text-red-600 flex-shrink-0" data-id="element-3178" />
                    <div data-id="element-3179">
                      <p className="font-mono text-xs text-red-600 uppercase tracking-widest" data-id="element-3180">
                        Method B
                      </p>
                      <p className="text-stone-50 font-medium text-sm mt-0.5" data-id="element-3181">
                        Net-Rate Delta
                      </p>
                    </div>
                  </div>
                  <div className="p-5 space-y-3 flex-1" data-id="element-3182">
                    <p className="text-sm text-stone-400" data-id="element-3183">
                      For each wholesale contract/channel, compare "what the
                      customer paid" vs "what the resort received"
                    </p>
                    <p className="text-sm text-stone-400" data-id="element-3184">
                      Leakage $ = Gross selling price − Net received
                    </p>
                    <p className="text-sm text-stone-400" data-id="element-3185">
                      If gross isn't visible, use contracted markup % or
                      conservative proxies (documented)
                    </p>
                  </div>
                  <div className="p-4 border-t border-stone-800 bg-stone-900/50" data-id="element-3186">
                    <p className="font-mono text-xs text-stone-500" data-id="element-3187">
                      <span className="text-stone-300" data-id="element-3188">Example:</span> Customer
                      price $500, resort net $400 → Leakage = $100 (20%)
                    </p>
                  </div>
                </motion.div>

                {/* Method C */}
                <motion.div custom={2} variants={itemVariants} initial="hidden" animate="visible" className="border border-stone-800 bg-stone-900/30 flex flex-col" data-id="element-3189">
                  <div className="p-5 border-b border-stone-800 flex items-center gap-3" data-id="element-3190">
                    <PieChart className="w-5 h-5 text-red-600 flex-shrink-0" data-id="element-3191" />
                    <div data-id="element-3192">
                      <p className="font-mono text-xs text-red-600 uppercase tracking-widest" data-id="element-3193">
                        Method C
                      </p>
                      <p className="text-stone-50 font-medium text-sm mt-0.5" data-id="element-3194">
                        Channel Mix Proxy
                      </p>
                    </div>
                    <span className="ml-auto font-mono text-[10px] text-amber-500 border border-amber-500/30 px-1.5 py-0.5 uppercase tracking-widest" data-id="element-3195">
                      Fastest
                    </span>
                  </div>
                  <div className="p-5 space-y-3 flex-1" data-id="element-3196">
                    <p className="text-sm text-stone-400" data-id="element-3197">
                      Leakage % ≈ (Wholesale mix % of group revenue) × (Average
                      wholesaler take %)
                    </p>
                    <p className="text-sm text-stone-400" data-id="element-3198">
                      Useful when finance data is incomplete during pilot
                    </p>
                    <p className="text-sm text-stone-400" data-id="element-3199">
                      Replaced with verified finance-line-item numbers after
                      launch
                    </p>
                  </div>
                  <div className="p-4 border-t border-stone-800 bg-stone-900/50" data-id="element-3200">
                    <p className="font-mono text-xs text-stone-500" data-id="element-3201">
                      <span className="text-stone-300" data-id="element-3202">Example:</span> 40%
                      wholesale mix × 20% take ≈ 8% leakage
                    </p>
                  </div>
                </motion.div>
              </div>
            </div>

            {/* Checklist + What Counts / Doesn't — side by side */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-3203">
              {/* Checklist */}
              <motion.div custom={3} variants={itemVariants} initial="hidden" animate="visible" className="border border-stone-800 bg-stone-900/30" data-id="element-3204">
                <div className="p-5 border-b border-stone-800" data-id="element-3205">
                  <p className="font-mono text-xs uppercase tracking-widest text-stone-500" data-id="element-3206">
                    What We Need From You
                  </p>
                  <p className="text-sm text-stone-400 mt-1" data-id="element-3207">
                    Last 12 months of data
                  </p>
                </div>
                <div className="p-5 space-y-3" data-id="element-3208">
                  {['Total Group Room Revenue (GRR)', 'Group revenue by channel/source (direct agency vs wholesale/bedbank/TO)', 'Total commissions/overrides/rebates tied to group bookings', 'List of net-rate wholesale contracts + terms (markup/discount logic)', 'Any marketing funds/kickbacks tied to wholesalers (if applicable)', 'Optional: attrition/cancellation impacts for group bookings'].map((item, i) => <div key={i} className="flex items-start gap-3" data-id="element-3209">
                      <div className="w-4 h-4 border border-stone-600 rounded-sm flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-3210">
                        <Square className="w-2.5 h-2.5 text-stone-700" data-id="element-3211" />
                      </div>
                      <p className="text-sm text-stone-300" data-id="element-3212">{item}</p>
                    </div>)}
                </div>
              </motion.div>

              {/* What Counts / Doesn't */}
              <motion.div custom={4} variants={itemVariants} initial="hidden" animate="visible" className="border border-stone-800 bg-stone-900/30" data-id="element-3213">
                <div className="p-5 border-b border-stone-800" data-id="element-3214">
                  <p className="font-mono text-xs uppercase tracking-widest text-stone-500" data-id="element-3215">
                    What Counts vs. What Doesn't
                  </p>
                  <p className="text-sm text-stone-400 mt-1" data-id="element-3216">
                    For Verified Savings calculation
                  </p>
                </div>
                <div className="grid grid-cols-2 divide-x divide-stone-800" data-id="element-3217">
                  <div className="p-5 space-y-3" data-id="element-3218">
                    <p className="font-mono text-xs text-emerald-500 uppercase tracking-widest mb-3" data-id="element-3219">
                      Counts
                    </p>
                    {['Reduced wholesaler/bedbank margin on routed group volume', 'Reduced commissions/overrides/rebates tied to wholesale group channels', 'Reduced net-rate discount leakage (where measurable)'].map((item, i) => <div key={i} className="flex items-start gap-2" data-id="element-3220">
                        <Check className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0 mt-0.5" data-id="element-3221" />
                        <p className="text-xs text-stone-300 leading-relaxed" data-id="element-3222">
                          {item}
                        </p>
                      </div>)}
                  </div>
                  <div className="p-5 space-y-3" data-id="element-3223">
                    <p className="font-mono text-xs text-stone-500 uppercase tracking-widest mb-3" data-id="element-3224">
                      Doesn't Count
                    </p>
                    {['Savings from unrelated operational changes', 'Revenue increases not tied to BlockQuote-adopted volume', 'Non-room revenue unless explicitly included (F&B, spa, excursions)'].map((item, i) => <div key={i} className="flex items-start gap-2" data-id="element-3225">
                        <X className="w-3.5 h-3.5 text-stone-600 flex-shrink-0 mt-0.5" data-id="element-3226" />
                        <p className="text-xs text-stone-400 leading-relaxed" data-id="element-3227">
                          {item}
                        </p>
                      </div>)}
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Trust Footnote + Download CTA */}
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 pt-4 border-t border-stone-800" data-id="element-3228">
              <p className="text-sm text-stone-500 max-w-2xl" data-id="element-3229">
                Verified Savings is measured monthly/quarterly against an agreed
                baseline. We only charge on savings that are measurable and
                reviewable with your finance team.
              </p>
              <button className="flex items-center gap-2 px-5 py-2.5 border border-stone-700 bg-stone-900 hover:bg-stone-800 text-stone-300 hover:text-stone-50 text-sm font-medium transition-colors flex-shrink-0" data-id="element-3230">
                <Download className="w-4 h-4" data-id="element-3231" />
                Download Appendix PDF
              </button>
            </div>
          </div>;
      default:
        return null;
    }
  };
  return <div className="h-screen w-full bg-stone-950 text-stone-50 overflow-hidden flex flex-col font-sans selection:bg-red-600/30" data-id="element-3232">
      {/* Top Bar */}
      <div className="flex items-center justify-between p-6 z-10 relative border-b border-stone-900/50" data-id="element-3233">
        <Link to="/" className="flex items-center text-stone-400 hover:text-stone-50 transition-colors font-mono text-sm uppercase tracking-widest" data-id="element-3234">
          <ArrowLeft className="w-4 h-4 sm:mr-2" data-id="element-3235" />
          <span className="hidden sm:inline" data-id="element-3236">Back to site</span>
        </Link>
        <div className="flex items-center gap-3" data-id="element-3237">
          <div className="w-5 h-5 bg-red-600 flex items-center justify-center" data-id="element-3238">
            <span className="font-serif text-xs font-bold text-white" data-id="element-3239">B</span>
          </div>
          <span className="font-mono text-xs uppercase tracking-widest text-stone-500" data-id="element-3240">
            Pilot for Lomas
          </span>
        </div>
        <div className="text-stone-500 font-mono text-sm" data-id="element-3241">
          {String(currentIndex + 1).padStart(2, '0')}{' '}
          <span className="text-stone-700" data-id="element-3242">/</span>{' '}
          {String(SLIDES.length).padStart(2, '0')}
        </div>
      </div>

      {/* Main Slide Area */}
      <div className="flex-1 relative flex items-start justify-center px-4 sm:px-8 md:px-16 lg:px-24 overflow-y-auto" data-id="element-3243">
        <AnimatePresence initial={false} custom={direction} mode="wait" data-id="element-3244">
          <motion.div key={currentIndex} custom={direction} variants={variants} initial="enter" animate="center" exit="exit" transition={{
          type: 'spring',
          stiffness: 300,
          damping: 30
        }} drag="x" dragConstraints={{
          left: 0,
          right: 0
        }} dragElastic={0.2} onDragEnd={(e, {
          offset,
          velocity
        }) => {
          const swipe = Math.abs(offset.x) * velocity.x;
          if (swipe < -10000 || offset.x < -100) paginate(1);else if (swipe > 10000 || offset.x > 100) paginate(-1);
        }} className="w-full max-w-6xl mx-auto pt-4 pb-8 md:pt-8 md:pb-12" data-id="element-3245">
            {renderSlideContent(slide)}
          </motion.div>
        </AnimatePresence>

        <button onClick={() => paginate(-1)} disabled={currentIndex === 0} className="hidden md:flex items-center justify-center p-4 text-stone-600 hover:text-stone-50 hover:bg-stone-900 disabled:opacity-0 transition-all border border-transparent hover:border-stone-800" data-id="element-3246">
          <ChevronLeft className="w-8 h-8" data-id="element-3247" />
        </button>
        <button onClick={() => paginate(1)} disabled={currentIndex === SLIDES.length - 1} className="hidden md:flex items-center justify-center p-4 text-stone-600 hover:text-stone-50 hover:bg-stone-900 disabled:opacity-0 transition-all border border-transparent hover:border-stone-800" data-id="element-3248">
          <ChevronRight className="w-8 h-8" data-id="element-3249" />
        </button>
      </div>

      {/* Progress */}
      <div className="z-10 relative border-t border-stone-900/50" data-id="element-3250">
        {/* Mobile nav: prev | counter | next */}
        <div className="flex md:hidden items-center justify-between px-4 py-3 gap-3" data-id="element-3251">
          <button onClick={() => paginate(-1)} disabled={currentIndex === 0} className="flex-1 flex items-center justify-center gap-2 py-3 border border-stone-800 text-stone-300 disabled:opacity-30 active:bg-stone-900 font-mono text-xs uppercase tracking-widest" data-id="element-3252">
            <ChevronLeft className="w-4 h-4" data-id="element-3253" /> Prev
          </button>
          <span className="font-mono text-xs text-stone-400 tabular-nums whitespace-nowrap" data-id="element-3254">
            {String(currentIndex + 1).padStart(2, '0')} /{' '}
            {String(SLIDES.length).padStart(2, '0')}
          </span>
          <button onClick={() => paginate(1)} disabled={currentIndex === SLIDES.length - 1} className="flex-1 flex items-center justify-center gap-2 py-3 border border-red-900/40 bg-red-950/20 text-stone-50 disabled:opacity-30 active:bg-red-950/40 font-mono text-xs uppercase tracking-widest" data-id="element-3255">
            Next <ChevronRight className="w-4 h-4" data-id="element-3256" />
          </button>
        </div>
        {/* Desktop progress dots */}
        <div className="hidden md:flex justify-center gap-3 p-6" data-id="element-3257">
          {SLIDES.map((_, i) => <button key={i} onClick={() => {
          setDirection(i > currentIndex ? 1 : -1);
          setCurrentIndex(i);
        }} className={`h-1 transition-all duration-300 ${i === currentIndex ? 'bg-red-600 w-8' : 'bg-stone-800 hover:bg-stone-600 w-4'}`} aria-label={`Go to slide ${i + 1}`} data-id="element-3258" />)}
        </div>
      </div>
    </div>;
}